/**
 * Headless state for the package create/edit form: every field, the entitlement
 * matrix grouped by category, the save-ability rule and
 * `POST/PATCH /api/admin/plans` with its error-code mapping. Returns no JSX -
 * each app renders its own dialog chrome.
 *
 * The matrix follows the picked service (`serviceKind`): a platform package edits
 * the platform catalog, a Veebihaldus package the monitoring catalog (it drives
 * site monitoring, docs/saidiseire-paketid.md), and any other package has none.
 */
import { useMemo, useState } from "react";
import { makeT } from "../i18n";
import type {
  ApiClient, EntitlementCatalogEntry, EntitlementMap, EntitlementValue,
  HighlightsMap, Locale, LocaleMap, PlanDTO, ProductDTO,
} from "../types";
import { cleanMap, errCodeStr, serviceKind, type ServiceKind } from "./shared";

/**
 * Category blocks of the entitlement matrix, in editor order: the platform
 * catalog's, then the monitoring catalog's.
 *
 * NOTE: this is a hand-written mirror of the API catalogs' categories and it does NOT match
 * `comparison-rows.ts` `CATEGORY_ORDER` (that one also lists "builder"). Kept exactly as the
 * editor had it - an unlisted category sorts to the front here, it is never dropped.
 */
const CATEGORY_ORDER = ["publishing", "content", "modules", "ai", "support", "monitoring", "wordpress", "client"];

/** A catalog row's value when the package does not set it: its own default, else off/zero. */
function fallbackValue(entry: EntitlementCatalogEntry): EntitlementValue {
  return entry.default ?? (entry.type === "boolean" ? false : 0);
}

/** Start the entitlement matrix from the catalog: every key present, missing ones at their default (else off/zero). */
export function buildFeatures(catalog: EntitlementCatalogEntry[], existing?: EntitlementMap): EntitlementMap {
  const out: EntitlementMap = {};
  for (const e of catalog) {
    const v = existing?.[e.key];
    out[e.key] = e.type === "boolean"
      ? (typeof v === "boolean" ? v : fallbackValue(e) === true)
      : typeof v === "number" ? v : fallbackValue(e);
  }
  return out;
}

/** Tidy the bullet list: trim + drop blank lines; a locale left empty is omitted. */
export function cleanHighlights(m: HighlightsMap): HighlightsMap {
  const out: HighlightsMap = {};
  for (const [loc, arr] of Object.entries(m)) {
    const lines = (arr ?? []).map((s) => s.trim()).filter(Boolean);
    if (lines.length) out[loc] = lines;
  }
  return out;
}

/** "19,90" / "19.90" -> 1990 cents; anything unparseable -> 0. */
export function eurToCents(v: string): number {
  const n = parseFloat(v.replace(",", "."));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export interface UsePlanFormOptions {
  api: ApiClient;
  locale: Locale;
  /** The platform catalog. */
  catalog: EntitlementCatalogEntry[];
  /** The monitoring catalog, for a package of the Veebihaldus service. */
  monitorCatalog?: EntitlementCatalogEntry[];
  products: ProductDTO[];
  /** `null` = create a new package. */
  plan: PlanDTO | null;
  /** Service a new package starts on (locked service -> active filter -> platform). */
  defaultProductCode: string;
  /** Set in the service detail view: the service picker is hidden and cannot change. */
  lockedProductCode?: string;
  onSaved: () => void;
}

export interface UsePlanFormResult {
  isNew: boolean;
  code: string;
  /** Lowercases the value - package codes are always lower case. */
  setCode: (value: string) => void;
  productCode: string;
  setProductCode: (code: string) => void;
  name: LocaleMap;
  setName: (next: LocaleMap) => void;
  description: LocaleMap;
  setDescription: (next: LocaleMap) => void;
  /** Content locale currently being edited (see `EDIT_LOCALES`). */
  activeLocale: string;
  setActiveLocale: (locale: string) => void;
  /** Prices stay strings so the number inputs can be emptied while typing. */
  priceMonthly: string;
  setPriceMonthly: (value: string) => void;
  priceYearly: string;
  setPriceYearly: (value: string) => void;
  currency: string;
  setCurrency: (value: string) => void;
  isPublic: boolean;
  setIsPublic: (value: boolean) => void;
  sortOrder: string;
  setSortOrder: (value: string) => void;
  trialDays: string;
  setTrialDays: (value: string) => void;
  /** Does the picked service gate platform entitlements? (Trial days are the platform's alone.) */
  isPlatformProduct: boolean;
  /** Does the picked service drive site monitoring (Veebihaldus)? Its matrix is the monitoring catalog. */
  isMonitoringProduct: boolean;
  /** What the picked service's packages do. */
  kind: ServiceKind;
  /** Render the service picker? (Hidden when the view is locked to one service.) */
  showProductPicker: boolean;
  features: EntitlementMap;
  setFeature: (key: string, value: EntitlementValue) => void;
  /** Current value of one catalog row, falling back to the type's default. */
  featureValue: (entry: EntitlementCatalogEntry) => EntitlementValue;
  /** The picked service's catalog grouped by category, in `CATEGORY_ORDER`; empty for a display-only service. */
  grouped: [string, EntitlementCatalogEntry[]][];
  highlights: HighlightsMap;
  /** Bullet list of the active content locale. */
  highlightLines: string[];
  updateHighlight: (index: number, value: string) => void;
  removeHighlight: (index: number) => void;
  addHighlight: () => void;
  featured: boolean;
  setFeatured: (value: boolean) => void;
  badge: LocaleMap;
  /** Badge text of the active content locale. */
  badgeText: string;
  setBadgeText: (value: string) => void;
  saving: boolean;
  /** Translated message for the last failed save, or `null`. */
  error: string | null;
  canSave: boolean;
  save: () => Promise<void>;
}

export function usePlanForm({
  api, locale, catalog, monitorCatalog = [], products, plan, defaultProductCode, lockedProductCode, onSaved,
}: UsePlanFormOptions): UsePlanFormResult {
  const t = makeT(locale);
  const isNew = plan === null;
  const kindOf = (code: string): ServiceKind => serviceKind(products.find((p) => p.code === code), code);
  const catalogOf = (kind: ServiceKind): EntitlementCatalogEntry[] =>
    kind === "platform" ? catalog : kind === "monitoring" ? monitorCatalog : [];
  const [code, setCodeRaw] = useState(plan?.code ?? "");
  const [productCode, setProductCodeRaw] = useState(plan?.productCode ?? defaultProductCode);
  const [name, setName] = useState<LocaleMap>(() => ({ ...(plan?.name ?? {}) }));
  const [description, setDescription] = useState<LocaleMap>(() => ({ ...(plan?.description ?? {}) }));
  const [activeLocale, setActiveLocale] = useState<string>("et");
  const [priceMonthly, setPriceMonthly] = useState(String((plan?.priceMonthlyCents ?? 0) / 100));
  const [priceYearly, setPriceYearly] = useState(String((plan?.priceYearlyCents ?? 0) / 100));
  const [currency, setCurrency] = useState(plan?.currency ?? "EUR");
  const [isPublic, setIsPublic] = useState(plan?.isPublic ?? true);
  const [sortOrder, setSortOrder] = useState(String(plan?.sortOrder ?? 0));
  const [trialDays, setTrialDays] = useState(String(plan?.trialDays ?? 0));
  const [features, setFeatures] = useState<EntitlementMap>(() =>
    buildFeatures(catalogOf(kindOf(plan?.productCode ?? defaultProductCode)), plan?.features),
  );
  // Display/marketing fields (display-only packages).
  const [highlights, setHighlights] = useState<HighlightsMap>(() => ({ ...(plan?.highlights ?? {}) }));
  const [featured, setFeatured] = useState(plan?.featured ?? false);
  const [badge, setBadge] = useState<LocaleMap>(() => ({ ...(plan?.badge ?? {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The picked service decides the matrix: the platform catalog, the monitoring one, or none.
  const kind = kindOf(productCode);
  const isPlatformProduct = kind === "platform";
  const isMonitoringProduct = kind === "monitoring";
  const activeCatalog = catalogOf(kind);
  // The service picker is hidden in the detail view (the service is already decided).
  const showProductPicker = !lockedProductCode && products.length > 0;

  const grouped = useMemo(() => {
    const m = new Map<string, EntitlementCatalogEntry[]>();
    for (const e of activeCatalog) {
      if (!m.has(e.category)) m.set(e.category, []);
      m.get(e.category)!.push(e);
    }
    return [...m.entries()].sort((a, b) => CATEGORY_ORDER.indexOf(a[0]) - CATEGORY_ORDER.indexOf(b[0]));
  }, [activeCatalog]);

  const canSave = (isNew ? /^[a-z0-9][a-z0-9_-]*$/.test(code) : true) && (name.et ?? "").trim().length > 0 && !saving;

  function setCode(value: string) {
    setCodeRaw(value.toLowerCase());
  }

  // Moving a package to a service of another kind swaps the matrix: the keys of the
  // old catalog mean nothing in the new one, so it starts from the new one's defaults.
  function setProductCode(next: string) {
    const nextKind = kindOf(next);
    if (nextKind !== kind) setFeatures(buildFeatures(catalogOf(nextKind), plan?.productCode === next ? plan.features : undefined));
    setProductCodeRaw(next);
  }

  function setFeature(key: string, value: EntitlementValue) {
    setFeatures((f) => ({ ...f, [key]: value }));
  }

  function featureValue(entry: EntitlementCatalogEntry): EntitlementValue {
    return features[entry.key] ?? fallbackValue(entry);
  }

  const highlightLines = highlights[activeLocale] ?? [];

  function setHighlightLines(lines: string[]) {
    setHighlights((h) => ({ ...h, [activeLocale]: lines }));
  }
  function updateHighlight(index: number, value: string) {
    const next = [...highlightLines];
    next[index] = value;
    setHighlightLines(next);
  }
  function removeHighlight(index: number) {
    setHighlightLines(highlightLines.filter((_, i) => i !== index));
  }
  function addHighlight() {
    setHighlightLines([...highlightLines, ""]);
  }

  const badgeText = badge[activeLocale] ?? "";
  function setBadgeText(value: string) {
    setBadge((b) => ({ ...b, [activeLocale]: value }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    const cleanedDesc = cleanMap(description);
    const cleanedBadge = cleanMap(badge);
    const body = {
      ...(isNew ? { code } : {}),
      productCode,
      name: cleanMap(name),
      description: Object.keys(cleanedDesc).length ? cleanedDesc : undefined,
      priceMonthlyCents: eurToCents(priceMonthly),
      priceYearlyCents: eurToCents(priceYearly),
      currency: currency.trim().toUpperCase() || "EUR",
      // A DISPLAY-ONLY service sends no features (the server clears them anyway).
      features: isPlatformProduct || isMonitoringProduct ? features : {},
      // Display/marketing fields - mainly for display-only packages.
      highlights: cleanHighlights(highlights),
      featured,
      badge: cleanedBadge,
      isPublic,
      sortOrder: parseInt(sortOrder, 10) || 0,
      // The trial applies ONLY to the platform package (checkout); display-only -> 0.
      trialDays: isPlatformProduct ? parseInt(trialDays, 10) || 0 : 0,
    };
    try {
      if (isNew) await api.post("/admin/plans", body);
      else await api.patch(`/admin/plans/${plan!.code}`, body);
      onSaved();
    } catch (e) {
      const c = errCodeStr(e);
      setError(c === "code_taken" ? t("editor.err.codeTaken") : t("editor.err.generic"));
    } finally {
      setSaving(false);
    }
  }

  return {
    isNew,
    code, setCode,
    productCode, setProductCode,
    name, setName,
    description, setDescription,
    activeLocale, setActiveLocale,
    priceMonthly, setPriceMonthly,
    priceYearly, setPriceYearly,
    currency, setCurrency,
    isPublic, setIsPublic,
    sortOrder, setSortOrder,
    trialDays, setTrialDays,
    isPlatformProduct, isMonitoringProduct, kind, showProductPicker,
    features, setFeature, featureValue, grouped,
    highlights, highlightLines, updateHighlight, removeHighlight, addHighlight,
    featured, setFeatured,
    badge, badgeText, setBadgeText,
    saving, error, canSave, save,
  };
}
